import { createAsyncThunk } from '@reduxjs/toolkit';
import { makeRequest } from '@/shared/services/http';
import {
    ICustomersGetResponse,
    ICustomersUserOnboardingResponse,
    CustomersStepTypeEnum,
    ICreditCard,
    CustomersController
} from './shared/endpoints/customers';
import { ITransferPrescriptions } from '@/shared/types/interfaces/transfer-prescriptions';
import { isNumberZeroOrGreater } from '@/shared/helpers/validations';
import { checkEligibilityAndSave, checkHasPendingEligibilityTest } from './shared/helpers/eligibility';
import { IDeliveryTicket, TransferStateEnum, IPendingPrescription } from './shared/types/interfaces';
import { cloneDeep } from 'lodash';
import { queryParamsForPAT } from './shared/endpoints/customers';

const ensurePreferredPaymentAndAddress = async (customer: ICustomersGetResponse) => {
    const hasAddedPaymentMethodsButNoneAreMarkedPreferred =
        customer.creditCards.length && customer.creditCards.every(item => !item.preferred);
    const promises = [];

    if (hasAddedPaymentMethodsButNoneAreMarkedPreferred) {
        const card = customer.creditCards[0];
        promises.push(makeRequest<ICreditCard>('patch', 'customers', 'credit-cards/:id/preferred', {}, { urlParams: { id: card.id } }));
    }

    if (checkHasPendingEligibilityTest(customer)) {
        promises.push(checkEligibilityAndSave({ customer }));
    }

    if (promises.length) {
        await Promise.all(promises);
        return makeRequest<ICustomersGetResponse>('get', 'customers', '');
    } else {
        return customer;
    }
};

const patExistsInNonInitiatedState = (patPendingPrescriptions: IPendingPrescription[]) => {
    const lastItem = queryParamsForPAT[queryParamsForPAT.length - 1]; // Get the last item
    const restItems = queryParamsForPAT.slice(0, -1); // Get all except the last item
    return (patPendingPrescriptions || []).some(pendingRx => {
        return (
            (pendingRx?.prescriptionActionType === lastItem && pendingRx?.transferPrescription?.state !== TransferStateEnum.INITIATED) ||
            restItems.includes(pendingRx?.prescriptionActionType)
        );
    });
};

export const onLoadSetupThunk = createAsyncThunk<ICustomersGetResponse, void, { rejectValue: Error }>(
    'data/onLoadCustomersSetup',
    async (_, { dispatch, rejectWithValue }) => {
        try {
            const promises = [
                makeRequest<ICustomersGetResponse>('get', 'customers', ''),
                dispatch(CustomersController.getPrescriptions()),
                dispatch(CustomersController.getPendingPrescriptions()),
                dispatch(CustomersController.getDeliveryTickets())
            ];
            const [response, payloadRxs, payloadPendingRxs, deliveryTickets] = await Promise.all(promises);

            if (
                payloadRxs.meta?.requestStatus !== 'fulfilled' ||
                payloadPendingRxs.meta?.requestStatus !== 'fulfilled' ||
                deliveryTickets.meta?.requestStatus !== 'fulfilled'
            ) {
                return rejectWithValue(new Error('Failed to set up'));
            }
            const prescriptions = payloadRxs.payload as ITransferPrescriptions[];
            const pendingPrescriptions = payloadPendingRxs.payload as IPendingPrescription[];
            const deliveryTicketsPayload = deliveryTickets.payload as IDeliveryTicket[];
            const customer = response as ICustomersGetResponse;

            if (customer?.userOnboarding?.details) {
                const hasFill = deliveryTicketsPayload?.some(item => item.deliveryItems.some(dI => dI.fill));
                const hasRxs = !!(prescriptions || []).length;
                const hasNonInitiatedPats = patExistsInNonInitiatedState(pendingPrescriptions || []);

                let stepToGoTo = null;
                const currentStep = customer?.userOnboarding?.details?.currentStep;
                if (currentStep !== CustomersStepTypeEnum.Done) {
                    if (hasNonInitiatedPats && !hasFill && !hasRxs && currentStep !== CustomersStepTypeEnum.ThankYou) {
                        stepToGoTo = CustomersStepTypeEnum.ThankYou;
                    } else if (hasFill || hasRxs) {
                        stepToGoTo = CustomersStepTypeEnum.Done;
                    }
                }

                if (stepToGoTo) {
                    const payload = cloneDeep(customer.userOnboarding!);

                    payload.details = { ...payload.details!, currentStep: stepToGoTo };
                    await dispatch(CustomersController.updateMetadata(payload));
                }
            } else {
                await makeRequest<ICustomersUserOnboardingResponse>('post', 'customers', 'onboarding', {
                    // note the step changes based on whether or noto hte user has a profile id. This will run if the user does not have onboarding state which is handled above.
                    details: {
                        currentStep: isNumberZeroOrGreater(customer?.userProfile?.id)
                            ? CustomersStepTypeEnum.Done
                            : CustomersStepTypeEnum.AboutYou,
                        visitedAboutYou: false,
                        visitedInsurance: false,
                        visitedMyMedbox: false,
                        phoneNumber: ''
                    }
                });
            }
            const customerNewRes = await makeRequest<ICustomersGetResponse>('get', 'customers', '');
            return await ensurePreferredPaymentAndAddress(customerNewRes);
        } catch (error) {
            return rejectWithValue(new Error('Failed to set up'));
        }
    }
);
